import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useRoles } from '@utils/hooks/use-roles.ts';
import { useConfirm } from '@utils/providers/confirm-context.tsx';
import {
  isPublishingDisabledError,
  useGetConnections
} from '@utils/api/connections/use-get-connections.tsx';
import { useDisconnectConnection } from '@utils/api/connections/use-disconnect-connection.tsx';
import type { Connection } from '@utils/types/connections/connection.ts';
import ConnectionsList from './connections-list';
import ConnectWizard from './connect-wizard/connect-wizard';

/**
 * Entry component for the Connections tab. Owns the wizard open/close
 * state and the disconnect confirmation. Hidden entirely when the org
 * does not have the `publishing` entitlement or when the user is not
 * an organization admin.
 */
export default function Connections() {
  const { t } = useTranslation();
  const { isAdmin } = useRoles();
  const { show: showConfirm } = useConfirm();

  const { data: connections, isLoading, error } = useGetConnections(isAdmin);
  const { mutate: disconnect } = useDisconnectConnection();

  // #2302: hide the whole area when the org has not enabled publishing.
  // The backend signals this with a 404 + ``publishing-not-enabled``,
  // not a feature flag on the entitlements response, so we branch on
  // the error shape instead of leaving the tab visible with a broken
  // "Connect a platform" CTA that will 404 the moment it is clicked.
  const publishingDisabled = isPublishingDisabledError(error);
  const canUsePublishing = isAdmin && !publishingDisabled;

  const [wizardOpen, setWizardOpen] = useState(false);
  const [reconnectFor, setReconnectFor] = useState<Connection | null>(null);

  const openWizard = useCallback(() => {
    setReconnectFor(null);
    setWizardOpen(true);
  }, []);

  const openReconnect = useCallback((connection: Connection) => {
    setReconnectFor(connection);
    setWizardOpen(true);
  }, []);

  const closeWizard = useCallback(() => {
    setWizardOpen(false);
    setReconnectFor(null);
  }, []);

  // The publish dialog's "Reconnect to upload new videos" alert routes
  // here with ``?reconnect=<connectionId>``. Pick that up as soon as
  // the connection list has loaded and open the wizard in reconnect
  // mode for the right row — otherwise the user lands on the page
  // with nothing happening. Clear the query param after so a refresh
  // doesn't re-trigger the modal.
  const [searchParams, setSearchParams] = useSearchParams();
  const reconnectId = searchParams.get('reconnect');
  useEffect(() => {
    if (!reconnectId || isLoading) return;
    const target = (connections ?? []).find(c => c.id === reconnectId);
    if (!target) return;
    openReconnect(target);
    const next = new URLSearchParams(searchParams);
    next.delete('reconnect');
    setSearchParams(next, { replace: true });
  }, [
    connections,
    isLoading,
    openReconnect,
    reconnectId,
    searchParams,
    setSearchParams
  ]);

  const handleDisconnect = useCallback(
    (connection: Connection) => {
      showConfirm({
        title: t('publishing-disconnect-confirm-title'),
        subtitle: t('publishing-disconnect-confirm-subtitle', {
          account: connection.account_name
        }),
        confirmText: t('publishing-disconnect'),
        cancelText: t('cancel'),
        type: 'warning',
        onConfirm: () => {
          disconnect(connection.id, {
            onSuccess: () => {
              toast.success(t('publishing-disconnect-success'));
            },
            onError: () => {
              toast.error(t('publishing-disconnect-failed'));
            }
          });
        }
      });
    },
    [disconnect, showConfirm, t]
  );

  if (!canUsePublishing) {
    return null;
  }

  return (
    <section
      aria-labelledby="publishing-connections-heading"
      className="flex flex-col gap-4"
    >
      <h2 id="publishing-connections-heading" className="sr-only">
        {t('publishing-connections-title')}
      </h2>

      <ConnectionsList
        connections={connections ?? []}
        isLoading={isLoading}
        onConnect={openWizard}
        onReconnect={openReconnect}
        onDisconnect={handleDisconnect}
      />

      <ConnectWizard
        isOpen={wizardOpen}
        onClose={closeWizard}
        reconnectFor={reconnectFor}
      />
    </section>
  );
}
